
class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
import { PopIn } from 'theme';
// Ensure PopIn is available as a global object
class ComparisonPopup extends PopIn {
  constructor() {
    super();
    this.thisComp = this.querySelector('.comp-wrap');
    this.slider = this.querySelector('[id^="swiper-"]');

    if (this.slider) {
      this.initSwiper();
    }

    // Listen for the variant:change event
    document.addEventListener("variant:change", this.onVariantChange.bind(this));

    // Listen for toggle-ajax button clicks
    this.addEventListener("click", this.onToggleAjaxClick.bind(this));

    // Check for the 'show_size=true' URL parameter to show the popin automatically
    if (this.shouldAppearAutomatically) {
      setTimeout(() => this.show(), this.apparitionDelay);
    }
  }

  get apparitionDelay() {
    return parseInt(this.getAttribute("apparition-delay") || 0) * 1e3;
  }

  get shouldAppearAutomatically() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("show_size") === "true";
  }

  onVariantChange(event) {
    const selectedVariant = event.detail.variant;
    const productHandle = this.querySelector('.product-data').dataset.handle;
    const varUrl = `/products/${productHandle}?variant=${selectedVariant.id}`;

    this.fetchAndUpdateComparison(varUrl);
  }

  onToggleAjaxClick(event) {
    const button = event.target.closest(".toggle-ajax");
    if (!button) return;

    const productUrl = button.dataset.product;
    if (!productUrl) return;

    this.fetchAndUpdateComparison(productUrl);
  }

  fetchAndUpdateComparison(url) {
    // Store the current active slide index
    let currentSlideIndex = this.swiperInstance?.activeIndex || 0;

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const newCompContent = responseHTML.querySelector('comparison-popup .comp-wrap');

        if (newCompContent) {
          const existingWaypointDiv = this.thisComp.querySelector('.comp-main--toggle-waypoints');
          const newWaypointDiv = newCompContent.querySelector('.comp-main--toggle-waypoints');

          if (existingWaypointDiv && newWaypointDiv) {
            // Preserve the existing waypoint div
            newWaypointDiv.replaceWith(existingWaypointDiv);

            // Switch the active class between the .toggle-waypoint buttons
            const buttons = existingWaypointDiv.querySelectorAll('.toggle-waypoint');
            if (buttons.length === 2) {
              buttons[0].classList.toggle('active');
              buttons[1].classList.toggle('active');
            }
          }

          // Update everything else
          HTMLUpdateUtility.setInnerHTML(this.thisComp, newCompContent.innerHTML);
          
          // Reinitialize Swiper if needed
          this.slider = this.thisComp.querySelector('[id^="swiper-"]');
          if (this.slider) {
            this.initSwiper();

            // Ensure swiperInstance is set before accessing it
            if (this.swiperInstance) {
              this.swiperInstance.slideTo(currentSlideIndex, 0, false); // Restore slide position
            }
          }
        }
      });
}


  

  initSwiper() {
    //console.log('running initSwiper')
    const slides = this.slider?.querySelectorAll('.swiper-slide');
    if (!slides || slides.length <= 1) {
      return;
    }

    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
    }

    this.swiperInstance = new Swiper(this.slider, {
      loop: true,
      slidesPerView: 1.1,
      spaceBetween: 6,
      navigation: {
        nextEl: '.swiper-button-next-custom',
        prevEl: '.swiper-button-prev-custom',
      },
      breakpoints: {
        700: {
          slidesPerView: 1
        }
      }
    });
  }
}

customElements.define("comparison-popup", ComparisonPopup);

// Define a new custom element for complementary products
class ComplementaryProducts extends HTMLElement {
  constructor() {
    super();
    
    // Listen for variant change events
    document.addEventListener("variant:change", this.onVariantChange.bind(this));
  }
  
  connectedCallback() {
    // Make sure the element is visible
    this.hidden = false;
  }
  
  onVariantChange(event) {
    // console.log('event::::', event)
    let selectedVariant;
    selectedVariant = event.detail.variant;
    //console.log(selectedVariant.title);
    
    // Get the product handle from the URL path
    const productHandle = window.location.pathname.split('/').filter(Boolean).pop();
    
    // Build the URL for fetching updated content
    const varUrl = `/products/${productHandle}?variant=${selectedVariant?.id}`;
    
    // Update the complementary products
    this.fetchAndUpdateProducts(varUrl);
  }
  
  fetchAndUpdateProducts(url) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        // Always get the current container reference, not stored from constructor
        const productsContainer = this.querySelector('.complementary-products');
        
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const newProductsContent = responseHTML.querySelector('complementary-products .complementary-products');
        
        if (newProductsContent && productsContainer) {
          try {
            // Use a more direct approach rather than relying on HTMLUpdateUtility
            // This way we won't lose reference to our elements
            const newContent = newProductsContent.innerHTML;
            productsContainer.innerHTML = newContent;
          } catch (error) {
            console.error("Error directly updating HTML:", error);
          }
        } else {
          console.warn("Could not find elements to update", {
            currentContainer: !!productsContainer,
            newContent: !!newProductsContent
          });
        }
      })
      .catch(error => {
        console.error("Error updating complementary products:", error);
      });
  }
}

// Register the custom element
customElements.define("complementary-products", ComplementaryProducts);




// Production: Mega Menu & Mobile Sidebar Video Handler
document.addEventListener('DOMContentLoaded', function() {
  
  // Handle mega menu videos
  setupMegaMenuVideos();
  
  // Handle mobile sidebar videos
  setupMobileSidebarVideos();
  
  function setupMegaMenuVideos() {
    const megaMenus = document.querySelectorAll('mega-menu-disclosure');
    
    megaMenus.forEach((menuElement) => {
      const detailsElement = menuElement.querySelector('details');
      if (!detailsElement) return;
      
      const videos = menuElement.querySelectorAll('.deferred-video[data-src]');
      if (videos.length === 0) return;
      
      // Watch for menu open/close state changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'aria-expanded') {
            const isOpen = detailsElement.getAttribute('aria-expanded') === 'true';
            
            if (isOpen) {
              loadAndPlayVideos(videos);
            } else {
              pauseVideos(videos);
            }
          }
        });
      });
      
      observer.observe(detailsElement, {
        attributes: true,
        attributeFilter: ['aria-expanded']
      });
      
      // Hover preloading for hover-triggered menus
      if (menuElement.getAttribute('trigger') === 'hover') {
        let hoverTimeout;
        
        detailsElement.addEventListener('mouseenter', () => {
          clearTimeout(hoverTimeout);
          hoverTimeout = setTimeout(() => {
            if (videos.length > 0) {
              preloadVideo(videos[0]);
            }
          }, 150);
        });
        
        detailsElement.addEventListener('mouseleave', () => {
          clearTimeout(hoverTimeout);
        });
      }
    });
  }
  
  function setupMobileSidebarVideos() {
    const sidebar = document.querySelector('header-sidebar');
    if (!sidebar) return;
    
    // Find all buttons that control panels with videos
    const panelButtons = sidebar.querySelectorAll('button[aria-controls]');
    
    panelButtons.forEach((button) => {
      const panelId = button.getAttribute('aria-controls');
      const panel = sidebar.querySelector(`#${panelId}`);
      
      if (!panel) return;
      
      // Find videos in this panel
      const videos = panel.querySelectorAll('.deferred-video[data-src]');
      if (videos.length === 0) return;
      
      // Watch for panel open/close state changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'aria-expanded') {
            const isOpen = button.getAttribute('aria-expanded') === 'true';
            
            if (isOpen) {
              loadAndPlayVideos(videos);
            } else {
              pauseVideos(videos);
            }
          }
        });
      });
      
      observer.observe(button, {
        attributes: true,
        attributeFilter: ['aria-expanded']
      });
    });
    
    // Also handle the main sidebar open/close
    const mainPanelVideos = sidebar.querySelectorAll('.header-sidebar__main-panel .deferred-video[data-src]');
    if (mainPanelVideos.length > 0) {
      const sidebarObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            const isOpen = sidebar.hasAttribute('open');
            
            if (isOpen) {
              // Sidebar opened - preload first video
              if (mainPanelVideos.length > 0) {
                preloadVideo(mainPanelVideos[0]);
              }
            } else {
              // Sidebar closed - pause all videos
              pauseVideos(mainPanelVideos);
              
              // Also pause videos in all sub-panels
              const allSidebarVideos = sidebar.querySelectorAll('.deferred-video[data-src]');
              pauseVideos(allSidebarVideos);
            }
          }
        });
      });
      
      sidebarObserver.observe(sidebar, {
        attributes: true,
        attributeFilter: ['open']
      });
    }
  }
  
  function loadAndPlayVideos(videos) {
    videos.forEach((video) => {
      initializeVideo(video, true);
    });
  }
  
  function pauseVideos(videos) {
    videos.forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });
  }
  
  function preloadVideo(video) {
    initializeVideo(video, false);
  }
  
  function initializeVideo(video, shouldPlay = false) {
    const videoSrc = video.getAttribute('data-src');
    if (!videoSrc) return;
    
    // Skip if already initialized
    if (video.querySelectorAll('source').length > 0) {
      if (shouldPlay && video.paused) {
        video.play().catch(() => {
          // Autoplay failed, but that's okay
        });
      }
      return;
    }
    
    // Create and add video source
    const source = document.createElement('source');
    source.src = videoSrc;
    source.type = 'video/mp4';
    video.appendChild(source);
    
    // Load the video
    video.load();
    
    // Handle successful load
    video.addEventListener('loadeddata', () => {
      // Hide fallback image
      const fallbackImage = video.parentNode.querySelector('.video-fallback');
      if (fallbackImage) {
        fallbackImage.style.transition = 'opacity 0.3s ease';
        fallbackImage.style.opacity = '0';
        setTimeout(() => {
          fallbackImage.style.display = 'none';
        }, 300);
      }
      
      // Auto-play if requested
      if (shouldPlay) {
        video.play().catch(() => {
          // Autoplay failed, but video is loaded
        });
      }
    });
    
    // Handle load errors silently
    video.addEventListener('error', () => {
      // Keep fallback image visible
    });
  }
  
});
// Define a new custom element for feature media lineup
    class FeatureMediaLineup extends HTMLElement {
      constructor() {
        super();
        
        // Listen for variant change events
        document.addEventListener("variant:change", this.onVariantChange.bind(this));
      }
      
      connectedCallback() {
        // Make sure the element is visible
        this.hidden = false;
      }
      
      onVariantChange(event) {
        const selectedVariant = event.detail.variant;
        //console.log('Feature lineup variant changed:', selectedVariant.title);
        
        // Get the product handle from the URL path
        const productHandle = window.location.pathname.split('/').filter(Boolean).pop();
        
        // Build the URL for fetching updated content
        const varUrl = `/products/${productHandle}?variant=${selectedVariant.id}`;
        
        // Update the feature media lineup
        this.fetchAndUpdateFeatures(varUrl);
      }
      
      fetchAndUpdateFeatures(url) {
        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            // Always get the current container reference
            const currentSection = this.querySelector('.section-spacing');
            
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const newSectionContent = responseHTML.querySelector('feature-media-lineup .section-spacing');
            
            if (newSectionContent && currentSection) {
              try {
                // Update the content directly
                const newContent = newSectionContent.innerHTML;
                currentSection.innerHTML = newContent;
              } catch (error) {
                console.error("Error updating feature media lineup:", error);
              }
            } else if (!newSectionContent) {
              // If no new content is found, it means the variant doesn't have the metafield
              // Hide the entire section
              this.style.display = 'none';
            } else {
              console.warn("Could not find elements to update", {
                currentSection: !!currentSection,
                newContent: !!newSectionContent
              });
            }
          })
          .catch(error => {
            console.error("Error updating feature media lineup:", error);
          });
      }
    }

    // Register the custom element
    customElements.define("feature-media-lineup", FeatureMediaLineup);

// Wait for the original MultipleMediaWithText to be defined first
function waitForBaseElement() {
  return new Promise((resolve) => {
    if (window.customElements.get('multiple-media-with-text')) {
      resolve();
    } else {
      const checkInterval = setInterval(() => {
        if (window.customElements.get('multiple-media-with-text')) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    }
  });
}

waitForBaseElement().then(() => {
  
class VariantResponsiveMediaWithText extends window.customElements.get('multiple-media-with-text') {
  constructor() {
    super();
    this.productId = this.getAttribute('product');
    this.sectionId = this.closest('.shopify-section')?.id || this.getAttribute('section-id');
    this.originalContent = null;
    this.isLoading = false;
  }

  connectedCallback() {
    // Call the parent's connectedCallback to preserve existing functionality
    super.connectedCallback();
    
    // Store the original content when first connected
    if (!this.originalContent) {
      this.originalContent = this.innerHTML;
    }

    // Listen for variant change events
    document.addEventListener('variant:change', this.handleVariantChange.bind(this));
  }

  disconnectedCallback() {
    // Clean up event listener
    document.removeEventListener('variant:change', this.handleVariantChange.bind(this));
  }

  async handleVariantChange(event) {
    // Since variant:change event doesn't include productId, we'll assume it's for our product
    // if we're on a product page. For collection pages, we'd need a different approach.
    const isProductPage = window.location.pathname.includes('/products/');
    
    if (!isProductPage) {
      return;
    }

    const newVariantId = event.detail.variant?.id;
    
    if (!newVariantId) {
      // No variant selected, revert to original content
      this.innerHTML = this.originalContent;
      return;
    }

    // Prevent multiple simultaneous requests
    if (this.isLoading) {
      return;
    }

    try {
      this.isLoading = true;
      await this.updateContent(newVariantId);
    } catch (error) {
      console.error('Error updating variant-responsive content:', error);
      // Fallback to original content on error
      this.innerHTML = this.originalContent;
    } finally {
      this.isLoading = false;
    }
  }

  async updateContent(variantId) {
    // Get the current page URL and add variant parameter
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('variant', variantId);

    try {
      // Fetch the page with the new variant
      const response = await fetch(currentUrl.toString());
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the corresponding element in the fetched content by section ID
      let newElement = null;
      
      if (this.sectionId) {
        // First try to find the exact section, then the element within it
        const sectionElement = doc.querySelector(`#${this.sectionId}`);
        if (sectionElement) {
          newElement = sectionElement.querySelector(`variant-responsive-media-with-text[product="${this.productId}"]`);
        }
      }
      
      // Fallback: if no section ID or not found, try direct selector (but this might cause the duplication issue)
      if (!newElement) {
        const allElements = doc.querySelectorAll(`variant-responsive-media-with-text[product="${this.productId}"]`);
        // If there are multiple elements, we can't safely determine which one matches this instance
        if (allElements.length === 1) {
          newElement = allElements[0];
        }
      }

      if (newElement) {
        // Update with the new content
        this.innerHTML = newElement.innerHTML;
        
        // Reinitialize any scripts that might be needed for the new content
        this.reinitializeScripts();
      } else {
        // If no variant-specific content found, show original
        this.innerHTML = this.originalContent;
      }

    } catch (error) {
      console.error('Error fetching variant content:', error);
      throw error;
    }
  }

  reinitializeScripts() {
    // Reinitialize video players
    const videos = this.querySelectorAll('video');
    videos.forEach(video => {
      if (video.hasAttribute('autoplay')) {
        video.play().catch(e => {
          // Autoplay might be blocked, that's okay
        });
      }
    });

    // Reinitialize any other custom elements within this section
    const customElements = this.querySelectorAll('[is], :not(:defined)');
    customElements.forEach(element => {
      if (element.connectedCallback && typeof element.connectedCallback === 'function') {
        element.connectedCallback();
      }
    });

    // Trigger a custom event to notify other scripts that content has been updated
    this.dispatchEvent(new CustomEvent('variant-responsive-media-with-text:updated', {
      bubbles: true,
      detail: { element: this }
    }));
  }
}

// Register the custom element
if (!customElements.get('variant-responsive-media-with-text')) {
  customElements.define('variant-responsive-media-with-text', VariantResponsiveMediaWithText);
}

});


// Hide unavailable product combinations
//
// Every query below is scoped to a single <variant-picker data-hide_unavailable_combinations>
// element ("scope") rather than the whole document. That scoping is what makes this work inside
// the Quick Buy modal: the modal injects its own product-info/variant-picker after page load, and
// a collection grid can hold many products at once. Document-wide lookups read the wrong product's
// data (or crash on a missing checked option) as soon as more than one picker is present.

document.addEventListener('DOMContentLoaded', function() {

  const PICKER_SELECTOR = '[data-hide_unavailable_combinations]';

  function show_hide_options(scope, applicable_variants, options, option_num) {
    options.forEach(el => {
      const current_value = el.dataset.label_value;
      const find_item = applicable_variants.find(variant => variant[option_num] == current_value)

      if (!find_item) {
        el.setAttribute('hidden', true)
        el.nextElementSibling?.setAttribute('hidden', true)
      } else {
        el.removeAttribute('hidden')
        el.nextElementSibling?.removeAttribute('hidden')
        // check for variant group, show if it's there
        el.closest('.variant-group')?.removeAttribute('hidden')
      }
    })

    const variant_groups = scope.querySelectorAll('.variant-group')
    if (variant_groups.length === 0) return;

    variant_groups.forEach(el => {
      const group_inputs = el.querySelectorAll('input');
      const group_inputs_hidden = el.querySelectorAll('input[hidden]');
      el.removeAttribute('style')
      if (group_inputs_hidden.length === 0) return;
      if (group_inputs.length === group_inputs_hidden.length) {
        el.setAttribute('hidden', true)
      }
    })
  }

  function hide_unavailable_combinations(scope) {
    if (!scope) return;

    const product_data_el = scope.querySelector('[data-product]')
    if (!product_data_el) return;

    let product_data;
    try {
      product_data = JSON.parse(product_data_el.textContent);
    } catch (e) {
      return;
    }
    if (!product_data || !Array.isArray(product_data.variants)) return;

    // Get the currently selected first option value (e.g. School). Guard against a missing
    // checked option: on sparse matrices (School + Color) the first available variant may not
    // leave option1 checked at the moment this runs, and reading .dataset off null throws.
    const current_option = scope.querySelector('[data-option-position="1"]:checked')
    if (!current_option) return;
    const current_option_label = current_option.dataset.label_value

    // Variants that share the currently selected first option value.
    const applicable_variants = product_data.variants.filter(item =>
      item.option1 === current_option_label
    );

    // Matched specifically on option2 / option3 (not a generic loop) so a value like "Black"
    // that appears in more than one position doesn't collide.
    const option2 = scope.querySelectorAll('[data-option-position="2"]')
    const option3 = scope.querySelectorAll('[data-option-position="3"]')

    if (option2.length > 0) show_hide_options(scope, applicable_variants, option2, 'option2')
    if (option3.length > 0) show_hide_options(scope, applicable_variants, option3, 'option3')
  }

  function showVariantGroups(scope) {
    (scope || document).querySelectorAll('.variant-group').forEach(el => {
      el.style.opacity = 1
    })
  }

  // An option input is "usable" if it's currently visible (not filtered out by hidden, not inside
  // a hidden variant-group) and not disabled (sold out when sold-out hiding is on).
  function is_usable(el) {
    if (el.hasAttribute('hidden') || el.disabled) return false;
    const group = el.closest('.variant-group');
    if (group && group.hasAttribute('hidden')) return false;
    return true;
  }

  // After the lower options are filtered for the current first option (e.g. School), make sure each
  // lower option (Color, etc.) has a usable value selected. When the previously-selected value gets
  // hidden, its radio stays checked-but-hidden and the theme reads an invalid combination — so we
  // click the first usable value to move the selection onto a real variant. No-op when a usable
  // value is already selected, so a valid user choice is never overridden. Works with or without
  // the variant_group metafield.
  function ensure_valid_selection(scope) {
    if (scope.__huc_fixing) return;
    scope.__huc_fixing = true;
    try {
      ['2', '3'].forEach(pos => {
        const inputs = Array.from(scope.querySelectorAll(`[data-option-position="${pos}"]`));
        if (inputs.length === 0) return;

        const usable = inputs.filter(is_usable);
        if (usable.length === 0) return;                 // nothing valid to select for this option
        if (usable.some(el => el.checked)) return;       // a valid value is already selected

        const target = usable[0];
        target.click();
        target.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } finally {
      scope.__huc_fixing = false;
    }
  }

  // Resolve the variant-picker scope for any node (input, event target, injected fragment).
  function getPicker(node) {
    return node && node.closest ? node.closest(PICKER_SELECTOR) : null;
  }

  // Apply the filter, repair the selection, and reveal groups for a single picker. Running
  // ensure_valid_selection here covers first paint and Quick Buy modal open, where these products
  // can render with a school chosen but no color selected.
  function initPicker(picker) {
    if (!picker) return;
    hide_unavailable_combinations(picker);
    ensure_valid_selection(picker);
    setTimeout(() => showVariantGroups(picker), 1);
  }

  function initAll(root) {
    (root || document).querySelectorAll(PICKER_SELECTOR).forEach(initPicker);
  }

  // 1) Pickers already in the live DOM at load (PDP, featured product).
  initAll(document);

  // 2) Pickers injected later (Quick Buy modal content, "load more" grids). A MutationObserver
  //    catches the picker whenever its markup is added to the page, so we don't depend on modal
  //    event names or bubbling.
  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches(PICKER_SELECTOR)) {
          initPicker(node);
        } else if (node.querySelectorAll) {
          node.querySelectorAll(PICKER_SELECTOR).forEach(initPicker);
        }
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // 3) Re-apply on variant change, scoped to the picker that changed (event bubbles from it).
  //    Refilter, then repair the selection if the current lower-option value got hidden.
  document.addEventListener('variant:change', (e) => {
    const picker = getPicker(e.target);
    if (!picker) return;
    showVariantGroups(picker);
    hide_unavailable_combinations(picker);
    ensure_valid_selection(picker);
  });

  // 4) When the first option (e.g. School) changes, the DOM `change` event always fires even if the
  //    resulting combination has no variant (which may suppress `variant:change`). Refilter the lower
  //    options for the new value, then move the selection onto the first usable value.
  document.addEventListener('change', (e) => {
    const change_target = e.target.closest('input')
    if (!change_target) return;
    const picker = getPicker(change_target);
    if (!picker) return;
    if (change_target.dataset.optionPosition !== '1' || !change_target.dataset.label_value) return;

    hide_unavailable_combinations(picker);
    ensure_valid_selection(picker);
  })
});

        
document.addEventListener('DOMContentLoaded', function() {
  function checkColor(colors) {
    for (const button of colors) {
      if (button.checked) {
        return true; // Found a checked radio button
      }
    }
    return false; // No radio buttons were checked
  }

  function selectColor(colors) {
    //console.log('colors', colors)
    const current_color = checkColor(colors);

    if (!current_color) colors[0].click();

  }

  document.addEventListener("variant:change", (e) => {
    // check color option first
    const colors = document.querySelectorAll('.option-color input[type="radio"]:not([hidden])')
    if (colors.length > 0) selectColor(colors)
  });

  const checkCollectionAltImages = document.querySelectorAll('[data-has_alt_variant]')
  console.log('checkCollectionAltImages', checkCollectionAltImages)
  if (checkCollectionAltImages.length > 0) {
    const altSwatches = document.querySelectorAll('[data-has_alt_variant] + label')
    console.log('altSwatches', altSwatches)
    if (altSwatches.length > 0) {
      altSwatches.forEach(el => el.classList.remove('is-disabled'))
    }
  }
});
/**
 * <addon-product>
 *
 * Renders a PDP cross-sell block with:
 *  - A toggle that opts into adding this product alongside the main product at checkout
 *  - Live variant sync with the main product's color selection
 *  - A modal ("View Details") with its own lightweight color picker
 *  - A "Change Color" affordance in the modal that overrides the synced color
 *
 * Relies on these theme behaviors:
 *  - Main product form dispatches "variant:change" events (from variant-picker.js)
 *  - <x-modal> is the theme's Modal custom element
 *  - /cart/add.js is used for sequential add-on submission (main first, addon second)
 */
class AddonProduct extends HTMLElement {
  constructor() {
    super();

    // State
    this.variants = [];
    this.currentVariant = null;
    this.colorOptionIndex = -1;
    this.userLockedColor = null; // If the user hit "Change Color", this overrides main-product sync
    this.mainForm = null;
    this.modal = null;

    // Bound handlers (for easy removal)
    this._onMainVariantChange = this._onMainVariantChange.bind(this);
    this._onMainFormSubmit = this._onMainFormSubmit.bind(this);
    this._onToggleChange = this._onToggleChange.bind(this);
    this._onSwatchClick = this._onSwatchClick.bind(this);
    this._onThumbnailClick = this._onThumbnailClick.bind(this);
    this._onChangeColorClick = this._onChangeColorClick.bind(this);
  }

  connectedCallback() {
    // Parse configuration
    this.colorOptionIndex = parseInt(this.getAttribute('color-option-index'), 10);
    const variantsScript = this.querySelector('script[data-addon-variants]');
    this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];

    const initialId = parseInt(this.getAttribute('initial-variant-id'), 10);
    this.currentVariant = this.variants.find((v) => v.id === initialId) || this.variants[0];

    // Cache DOM references
    this.toggleInput = this.querySelector('[data-addon-toggle]');
    this.thumbnailImg = this.querySelector('[data-addon-thumbnail]');
    this.priceWrapper = this.querySelector('[data-addon-price]');
    this.colorSuffix = this.querySelector('[data-addon-color-suffix]');

    // Modal references
    const modalId = this.getAttribute('modal-id');
    this.modal = modalId ? document.getElementById(modalId) : null;

    if (this.modal) {
      this.modalCarousel = this.modal.querySelector('[data-addon-modal-carousel]');
      this.modalPrice = this.modal.querySelector('[data-addon-modal-price]');
      this.modalSelectedColor = this.modal.querySelector('[data-addon-modal-selected-color]');
      this.modalSwatches = Array.from(this.modal.querySelectorAll('[data-addon-swatch]'));
      this.modalThumbnails = Array.from(this.modal.querySelectorAll('[data-addon-thumbnail-button]'));
      this.modalChangeColorLink = this.modal.querySelector('[data-addon-change-color]');
      this.modalSoldOut = this.modal.querySelector('[data-addon-sold-out]');

      // The variant currently being *previewed* inside the modal — may differ
      // from this.currentVariant (the outside selection) until the user confirms
      // with "Change Color".
      this.modalPreviewVariant = this.currentVariant;
    }

    // Main product form (for variant sync + submit interception)
    const mainFormId = this.getAttribute('main-form-id');
    this.mainForm = mainFormId ? document.forms[mainFormId] : null;

    // Wire listeners
    if (this.mainForm) {
      // console.log('this.mainForm', this.mainForm)
      this.mainForm.addEventListener('variant:change', this._onMainVariantChange);
      // Capture-phase so we run before ProductForm.onSubmit
      // editing for plural addons
      // this.mainForm.addEventListener('submit', this._onMainFormSubmit, { capture: true });
      if (!this.mainForm._addonSubmitAttached) {
        this.mainForm.addEventListener('submit', this._onMainFormSubmit, { capture: true });
        this.mainForm._addonSubmitAttached = true;
      }
    }

    if (this.toggleInput) {
      this.toggleInput.addEventListener('change', this._onToggleChange);
    }

    this.modalSwatches?.forEach((btn) => btn.addEventListener('click', this._onSwatchClick));
    this.modalThumbnails?.forEach((btn) => btn.addEventListener('click', this._onThumbnailClick));
    this.modalChangeColorLink?.addEventListener('click', this._onChangeColorClick);

    // Sync the active thumbnail as the user scrolls/swipes the carousel.
    // Debounced to avoid thrashing on every scroll event.
    if (this.modalCarousel) {
      this._onCarouselScroll = this._debounce(this._onCarouselScroll.bind(this), 80);
      this.modalCarousel.addEventListener('scroll', this._onCarouselScroll);
    }

    // Reset the modal preview to the outside state every time the modal opens.
    // We listen to dialog:before-show rather than intercepting the "View Details"
    // click, because the theme's DialogElement already handles opening via
    // aria-controls and we don't want to fight it.
    this._onModalBeforeShow = this._onModalBeforeShow.bind(this);
    this.modal?.addEventListener('dialog:before-show', this._onModalBeforeShow);

    // Pre-filter carousel slides and thumbnails for the initial color so the
    // modal opens in the correct state the first time.
    this._updateModalDisplay();

  }

  disconnectedCallback() {
    if (this.mainForm) {
      this.mainForm.removeEventListener('variant:change', this._onMainVariantChange);
      // editing for plural addons
      // this.mainForm.removeEventListener('submit', this._onMainFormSubmit, { capture: true });
      if (this.mainForm._addonSubmitAttached) {
        this.mainForm.removeEventListener('submit', this._onMainFormSubmit, { capture: true });
        this.mainForm._addonSubmitAttached = false;
      }
    }
    this.toggleInput?.removeEventListener('change', this._onToggleChange);
    this.modalSwatches?.forEach((btn) => btn.removeEventListener('click', this._onSwatchClick));
    this.modalThumbnails?.forEach((btn) => btn.removeEventListener('click', this._onThumbnailClick));
    this.modalChangeColorLink?.removeEventListener('click', this._onChangeColorClick);
    this.modalCarousel?.removeEventListener('scroll', this._onCarouselScroll);
    this.modal?.removeEventListener('dialog:before-show', this._onModalBeforeShow);
  }

  // ====================================================================
  //                         PUBLIC-ISH GETTERS
  // ====================================================================

  get isToggleOn() {
    return !!(this.toggleInput && this.toggleInput.checked);
  }

  // ====================================================================
  //                         VARIANT MATCHING
  // ====================================================================

  /**
   * Find a variant matching the given color value (exact match).
   * Prefers available variants; falls back to any variant if none available.
   *
   * Use this for the MODAL swatches, where we deliberately allow previewing a
   * sold-out color. For auto-syncing to the main product, use
   * _findAvailableVariantByColor instead so we never surface a sold-out match.
   */
  _findVariantByColor(colorValue) {
    if (this.colorOptionIndex < 0 || !colorValue) return null;

    const available = this.variants.find(
      (v) => v.available && v.options[this.colorOptionIndex] === colorValue
    );
    if (available) return available;

    return this.variants.find((v) => v.options[this.colorOptionIndex] === colorValue) || null;
  }

  /**
   * Find an AVAILABLE variant matching the given color value (exact match).
   * Returns null if the color doesn't exist OR every variant in that color is
   * sold out. Works whether color is the only option or one of several
   * (it just looks at the color option index and the variant's availability).
   *
   * This is what the main-product auto-sync uses, so a sold-out color match
   * falls through to the first-available fallback instead of being displayed.
   */
  _findAvailableVariantByColor(colorValue) {
    if (this.colorOptionIndex < 0 || !colorValue) return null;

    return (
      this.variants.find(
        (v) => v.available && v.options[this.colorOptionIndex] === colorValue
      ) || null
    );
  }

  /**
   * Return a safe fallback variant (first available).
   */
  _firstAvailableVariant() {
    return this.variants.find((v) => v.available) || this.variants[0];
  }

  // ====================================================================
  //                         MAIN PRODUCT SYNC
  // ====================================================================

  _onMainVariantChange(event) {
    // If the user has locked the color via "Change Color", ignore main product syncs
    if (this.userLockedColor) return;

    const mainColor = this._getColorFromVariantEvent(event);
    if (!mainColor) return;

    // Available-only match: if the main product's color is sold out on the
    // addon, skip it and fall back to the first available variant rather than
    // surfacing a sold-out color as the matched selection.
    const match = this._findAvailableVariantByColor(mainColor) || this._firstAvailableVariant();
    if (match && match.id !== this.currentVariant.id) {
      this._setOutsideVariant(match);
    }
  }

  /**
   * Pull the color value out of a variant:change event.
   *
   * Rather than trying to detect which option index is "color" on the main
   * product (which is fragile across themes and depends on DOM labels), we
   * just scan the event's options array for any value that matches a known
   * color of our add-on product. This works because color names (Black,
   * Blush, Cloud...) are very unlikely to collide with size/material values.
   */
  _getColorFromVariantEvent(event) {
    const variant = event?.detail?.variant;
    if (!variant || !Array.isArray(variant.options)) return null;

    if (this.colorOptionIndex < 0) return null;

    // Build a set of known color values for the add-on
    const knownColors = new Set(
      this.variants.map((v) => v.options[this.colorOptionIndex])
    );

    // Find the first option on the main variant that matches a known add-on color
    for (const optionValue of variant.options) {
      if (knownColors.has(optionValue)) {
        return optionValue;
      }
    }

    return null;
  }

  /**
   * Update the outside (toggle area) display AND the modal's preview state.
   * This is the canonical "what variant is selected" update.
   */
  _setOutsideVariant(variant) {
    this.currentVariant = variant;

    // Thumbnail
    if (this.thumbnailImg && variant.featured_image?.src) {
      this.thumbnailImg.src = variant.featured_image.src;
      this.thumbnailImg.srcset = '';
      this.thumbnailImg.alt = variant.featured_image.alt || '';
    }

    // Color suffix in title (e.g. "- Blush")
    if (this.colorSuffix && this.colorOptionIndex >= 0) {
      this.colorSuffix.textContent = ' - ' + variant.options[this.colorOptionIndex];
    }

    // Price — use Shopify's formatMoney if available, else fall back
    this._updatePriceDisplay(this.priceWrapper, variant);

    // Keep the modal preview in sync with the outside when user hasn't diverged
    this.modalPreviewVariant = variant;
    this._updateModalDisplay();
    this._updateChangeColorLink();
  }

  _updatePriceDisplay(wrapper, variant) {
    if (!wrapper) return;
    const priceEl = wrapper.querySelector('.price-list__price, .price, [data-price]');
    const formatted = this._formatMoney(variant.price);

    if (priceEl) {
      // Replace just the price text without clobbering the whole price-list markup
      // (which may contain compare-at price / unit price structure).
      // Text nodes are replaced; currency wrappers kept.
      priceEl.textContent = formatted;
    } else {
      // Last-resort fallback
      wrapper.querySelector(':scope > :last-child')?.replaceChildren(
        document.createTextNode(formatted)
      );
    }
  }

  _formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.themeVariables?.moneyFormat);
    }
    // Minimal fallback
    const amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

  // ====================================================================
  //                         TOGGLE
  // ====================================================================

  _onToggleChange() {
    this.classList.toggle('is-enabled', this.isToggleOn);
  }

  // ====================================================================
  //                         MODAL: SWATCHES & PREVIEW
  // ====================================================================

  _onSwatchClick(event) {
    const btn = event.currentTarget;
    const colorValue = btn.getAttribute('data-color-value');
    if (!colorValue) return;

    const match = this._findVariantByColor(colorValue);
    if (!match) return;

    this.modalPreviewVariant = match;
    this._updateModalDisplay();
    this._updateChangeColorLink();
  }

  /**
   * Thumbnail click — scroll the carousel to the matching slide.
   */
  _onThumbnailClick(event) {
    event.preventDefault();
    const btn = event.currentTarget;
    const position = btn.getAttribute('data-media-position');
    if (!position || !this.modalCarousel) return;

    const allSlides = this.modalCarousel.allCells || [];
    const targetSlide = allSlides.find((s) => s.getAttribute('data-media-position') === position);
    if (!targetSlide) return;

    const visibleCells = this.modalCarousel.cells || [];
    const visibleIndex = visibleCells.indexOf(targetSlide);
    if (visibleIndex >= 0) {
      this.modalCarousel.select(visibleIndex);
    }

    // Optimistically set active state — the scroll listener will re-confirm
    this._setActiveThumbnailByPosition(position);
  }

  /**
   * Carousel scrolled/swiped — update which thumbnail is marked active.
   * Finds the slide closest to the carousel's horizontal center.
   */
  _onCarouselScroll() {
    if (!this.modalCarousel) return;
    const carouselRect = this.modalCarousel.getBoundingClientRect();
    const carouselCenterX = carouselRect.left + carouselRect.width / 2;

    const visibleCells = this.modalCarousel.cells || [];
    let closestSlide = null;
    let closestDistance = Infinity;

    visibleCells.forEach((cell) => {
      const rect = cell.getBoundingClientRect();
      const cellCenterX = rect.left + rect.width / 2;
      const distance = Math.abs(cellCenterX - carouselCenterX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlide = cell;
      }
    });

    if (closestSlide) {
      const position = closestSlide.getAttribute('data-media-position');
      if (position) this._setActiveThumbnailByPosition(position);
    }
  }

  /**
   * Set aria-current="true" on the thumbnail matching the given media position,
   * and "false" on all others. Scrolls the active thumb into view horizontally.
   */
  _setActiveThumbnailByPosition(position) {
    let activeThumb = null;
    this.modalThumbnails.forEach((thumb) => {
      const isMatch = thumb.getAttribute('data-media-position') === position;
      thumb.setAttribute('aria-current', isMatch ? 'true' : 'false');
      if (isMatch) activeThumb = thumb;
    });
    if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  /**
   * Simple debounce helper.
   */
  _debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /**
   * Refresh all modal UI based on this.modalPreviewVariant.
   */
  _updateModalDisplay() {
    if (!this.modal || !this.modalPreviewVariant) return;
    const v = this.modalPreviewVariant;
    const activeColor = this.colorOptionIndex >= 0 ? v.options[this.colorOptionIndex] : null;

    // Filter the carousel slides AND thumbnails to only show images matching
    // the active color (plus "all"-tagged images that apply regardless).
    if (this.modalCarousel && activeColor) {
      const activeColorLower = activeColor.toLowerCase();
      const allSlides = this.modalCarousel.allCells;
      const indexesToHide = [];
      let firstVisibleIndex = -1;
      let featuredMatchIndex = -1;

      allSlides.forEach((slide, index) => {
        const colorsAttr = (slide.getAttribute('data-media-colors') || '').toLowerCase();
        const mediaId = slide.getAttribute('data-media-id');
        const matches = colorsAttr === 'all' || colorsAttr === activeColorLower;

        if (!matches) {
          indexesToHide.push(index);
        } else {
          if (firstVisibleIndex === -1) firstVisibleIndex = index;
          if (v.featured_image && String(v.featured_image.id) === mediaId) {
            featuredMatchIndex = index;
          }
        }
      });

      // Hide non-matching slides in the carousel via the theme's filter API
      this.modalCarousel.filter(indexesToHide);

      // Hide non-matching thumbnails manually (we don't rely on
      // product-gallery-navigation since we're not using it here)
      this.modalThumbnails.forEach((thumb) => {
        const colorsAttr = (thumb.getAttribute('data-media-colors') || '').toLowerCase();
        const matches = colorsAttr === 'all' || colorsAttr === activeColorLower;
        thumb.hidden = !matches;
      });

      // Scroll to the preferred slide: featured-image match, or first visible
      const targetIndex = featuredMatchIndex >= 0 ? featuredMatchIndex : firstVisibleIndex;
      if (targetIndex >= 0) {
        const visibleCells = this.modalCarousel.cells;
        const targetCell = allSlides[targetIndex];
        const visibleIndex = visibleCells.indexOf(targetCell);
        if (visibleIndex >= 0) {
          this.modalCarousel.select(visibleIndex, { instant: true });
          // Sync the active thumb to the slide we just scrolled to
          const position = targetCell.getAttribute('data-media-position');
          if (position) this._setActiveThumbnailByPosition(position);
        }
      }
    }

    // Price
    this._updatePriceDisplay(this.modalPrice, v);

    // Selected color label
    if (this.modalSelectedColor && activeColor) {
      this.modalSelectedColor.textContent = activeColor;
    }

    // Active swatch
    if (activeColor) {
      this.modalSwatches.forEach((sw) => {
        const isActive = sw.getAttribute('data-color-value') === activeColor;
        sw.classList.toggle('is-active', isActive);
        sw.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });
    }
  }

  _updateChangeColorLink() {
    const previewId = this.modalPreviewVariant?.id;
    const outsideId = this.currentVariant?.id;
    const available = !!this.modalPreviewVariant?.available;

    if (!available) {
      // Sold-out color previewed: show "Sold Out" where the CTA would sit, and
      // never offer to commit it as the addon selection.
      if (this.modalChangeColorLink) this.modalChangeColorLink.hidden = true;
      if (this.modalSoldOut) this.modalSoldOut.hidden = false;
      return;
    }

    if (this.modalSoldOut) this.modalSoldOut.hidden = true;
    if (!this.modalChangeColorLink) return;

    // Only offer "Change Color" when the preview differs from the current
    // (available) selection.
    const shouldShow = previewId && outsideId && previewId !== outsideId;
    this.modalChangeColorLink.hidden = !shouldShow;
  }

  _onChangeColorClick(event) {
    event.preventDefault();
    if (!this.modalPreviewVariant) return;

    // Safety net: if the previewed color is sold out, do not commit or enable it.
    if (!this.modalPreviewVariant.available) return;

    // Commit the preview to the outside selection and lock it
    this._setOutsideVariant(this.modalPreviewVariant);

    // Lock to this color permanently — only another "Change Color" action
    // from the user can override this
    if (this.colorOptionIndex >= 0) {
      this.userLockedColor = this.modalPreviewVariant.options[this.colorOptionIndex];
    }

    // Auto-enable the toggle — the user explicitly confirmed this color,
    // so they presumably want this add-on in their cart
    if (this.toggleInput && !this.toggleInput.checked) {
      this.toggleInput.checked = true;
      this._onToggleChange();
    }

    // Close the modal
    this.modal?.hide?.();
  }

  _onModalBeforeShow() {
    // Reset the preview to whatever is currently selected outside
    this.modalPreviewVariant = this.currentVariant;
    this._updateModalDisplay();
    this._updateChangeColorLink();
  }

  // ====================================================================
  //                         CART SUBMIT INTERCEPT (1a+2c)
  // ====================================================================

  /**
   * Before the theme's ProductForm.onSubmit runs:
   *   - If toggle is off, do nothing (normal flow proceeds).
   *   - If toggle is on, stop the normal flow, do main-then-addon sequentially,
   *     and dispatch the same events ProductForm would dispatch so the cart
   *     drawer / banners update normally.
   */
  async _onMainFormSubmit(event) {
    // editing for plural addons 
    // if (!this.isToggleOn || !this.currentVariant) return;
    const allAddons = Array.from(document.querySelectorAll('addon-product'));
    // Final safety net: never submit a sold-out addon variant, even if one
    // somehow became the current selection.
    const enabledAddons = allAddons.filter(
      a => a.isToggleOn && a.currentVariant && a.currentVariant.available
    );
    if (enabledAddons.length === 0) return;
    if (event.defaultPrevented) return;

    // Stop the theme handler from also running
    event.preventDefault();
    event.stopImmediatePropagation();

    const form = this.mainForm;
    const submitButtons = Array.from(form.elements).filter((el) => el.type === 'submit');

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    submitButtons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:start', { bubbles: true })
    );

    // Gather section bundles the theme wants refreshed
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const formData = new FormData(form);
    formData.set('sections', sectionsToBundle.join(','));
    formData.set('sections_url', `${Shopify.routes.root}variants/${form.id.value}`);

    // ============ 1) Add the main product ============
    let mainResponseJson;
    try {
      const mainResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      mainResponseJson = await mainResponse.json();

      if (!mainResponse.ok) {
        submitButtons.forEach((b) => b.removeAttribute('aria-busy'));
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:end', { bubbles: true })
        );
        form.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: mainResponseJson['description'] || 'Error adding to cart' },
          })
        );
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }
    } catch (err) {
      submitButtons.forEach((b) => b.removeAttribute('aria-busy'));
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:end', { bubbles: true })
      );
      return;
    }

    // ============ 2) Add all enabled addons sequentially (best-effort) ============
    let addonFailedMessage = null;
    let addonResponseJson = null;

    for (const addon of enabledAddons) {
      try {
        const addonResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: addon.currentVariant.id,
            quantity: 1,
            sections: sectionsToBundle.join(','),
            sections_url: `${Shopify.routes.root}variants/${addon.currentVariant.id}`,
          }),
        });

        addonResponseJson = await addonResponse.json().catch(() => ({}));

        if (!addonResponse.ok) {
          addonFailedMessage = addonResponseJson.description || 'An add-on could not be added to your cart.';
        }
      } catch (err) {
        addonFailedMessage = 'An add-on could not be added to your cart.';
      }
    }

    // ============ 3) Fetch fresh cart + use freshest sections ============
    //
    // Prefer the addon response's sections (freshest — shows both items).
    // If addon add failed, fall back to main response sections (shows just
    // main, which is correct since that's the actual cart state).
    const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cartContent['sections'] =
      (addonResponseJson && addonResponseJson['sections']) || mainResponseJson['sections'];

    submitButtons.forEach((b) => b.removeAttribute('aria-busy'));
    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:end', { bubbles: true })
    );

    // ============ 4) Dispatch the same events ProductForm would ============
    form.dispatchEvent(
      new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items: mainResponseJson.hasOwnProperty('items')
            ? mainResponseJson['items']
            : [mainResponseJson],
          cart: cartContent,
          onSuccessDo: formData.get('on_success'),
        },
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          onSuccessDo: formData.get('on_success'),
          cart: cartContent,
        },
      })
    );

    // ============ 5) If addon failed, show a warning (2c) ============
    if (addonFailedMessage) {
      form.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: addonFailedMessage },
        })
      );
    }

    // Reset the toggle so a subsequent add doesn't silently duplicate
    // ADD — reset all addon toggles:
    allAddons.forEach(addon => {
      if (addon.toggleInput) {
        addon.toggleInput.checked = false;
        addon._onToggleChange();
      }
    });
  }
  
}

if (!window.customElements.get('addon-product')) {
  window.customElements.define('addon-product', AddonProduct);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('quick-buy-modal').forEach(modal => {
    modal.addEventListener('dialog:after-show', () => {
      // console.log('heard dialog:after-show')
      const carousel = modal.querySelector('scroll-carousel');
      if (!carousel) return;

      const prev = modal.querySelector('carousel-prev-button');
      const next = modal.querySelector('carousel-next-button');

      if (prev) {
        prev.querySelector('button').addEventListener('click', () => carousel.previous());
      }
      if (next) {
        next.querySelector('button').addEventListener('click', () => carousel.next());
      }
    });
  });


  document.addEventListener('click', (event) => {
    // Find if a combined-product option link was clicked inside a quick-buy modal
    const link = event.target.closest('a[href*="/products/"]');
    if (!link) return;

    const modal = link.closest('quick-buy-modal');
    if (!modal) return;

    event.preventDefault();

    // Extract the product handle from the href
    const url = new URL(link.href);
    const pathParts = url.pathname.split('/').filter(Boolean);
    const productsIndex = pathParts.indexOf('products');
    if (productsIndex === -1) return;

    // handle may include ?variant=... so just take the path segment
    const newHandle = pathParts[productsIndex + 1];
    if (!newHandle) return;

    // Update the modal's handle and reload it
    // Need to preserve variant if present
    const variantParam = url.searchParams.get('variant');
    const fullHandle = variantParam 
      ? `${newHandle}?variant=${variantParam}` 
      : newHandle;

    modal.setAttribute('handle', fullHandle);
    modal.show();
  });

  // Carry the grid's selected color into Quick Buy. When a product-card swatch changes, the theme
  // updates the card's product links to that variant but leaves the quick-buy-modal handle on the
  // product default. Point the modal at the chosen variant so it opens on the color the shopper
  // picked. QuickBuyModal.show() reads `handle` (with ?variant=) at open time, so setting it here
  // is enough — no need to pre-fetch.
  document.addEventListener('change', (event) => {
    const swatch = event.target.closest('product-card [type="radio"]');
    if (!swatch || !swatch.dataset.variantId) return;
    const modal = swatch.closest('product-card')?.querySelector('quick-buy-modal');
    if (!modal) return;
    const handleBase = (modal.getAttribute('handle') || '').split('?')[0];
    if (!handleBase) return;
    modal.setAttribute('handle', `${handleBase}?variant=${swatch.dataset.variantId}`);
  });
});

/* ------------------------------------------------------------------
 * Accessibility (WCAG 2.2.2): pause/play control for auto-playing
 * videos. Covers two control types via event delegation:
 *   - .slideshow__playback-control  (homepage slideshow, sibling of <video-media>)
 *   - .video-playback-control       (centralized, rendered inside <video-media>
 *                                     by snippets/media.liquid — PDP gallery,
 *                                     multiple-media-with-text, multi-column, etc.)
 * The theme (theme.js) plays/pauses videos on slide change, so we also
 * sync the button state from the video's own play/pause events.
 * ------------------------------------------------------------------ */
(function () {
  var CONTROL_SELECTOR = '.slideshow__playback-control, .video-playback-control';

  // Find the video(s) a control governs. The centralized control lives inside
  // <video-media>; the slideshow's control is a sibling of <video-media> within
  // the slide. Only act on the on-screen video (ignores the hidden responsive
  // desktop/mobile variant), matching the theme's own logic.
  function getVideos(control) {
    var mediaEl = control.closest('video-media');
    if (mediaEl) {
      return Array.from(mediaEl.querySelectorAll('video')).filter(function (v) { return v.offsetParent !== null; });
    }
    var slide = control.closest('.slideshow__slide');
    if (slide) {
      return Array.from(slide.querySelectorAll('video')).filter(function (v) { return v.offsetParent !== null; });
    }
    return [];
  }

  function syncButtons(control, isPaused) {
    var pauseBtn = control.querySelector('[data-action="pause-video"]');
    var playBtn = control.querySelector('[data-action="play-video"]');
    if (pauseBtn) pauseBtn.hidden = isPaused;
    if (playBtn) playBtn.hidden = !isPaused;
  }

  // Toggle playback on click (event-delegated so it covers every context).
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-action="pause-video"], [data-action="play-video"]');
    if (!button) return;

    var control = button.closest(CONTROL_SELECTOR);
    if (!control) return;

    event.preventDefault();

    var shouldPause = button.getAttribute('data-action') === 'pause-video';
    var videos = getVideos(control);
    if (!videos.length) return;

    videos.forEach(function (video) {
      if (shouldPause) {
        video.pause();
      } else {
        var result = video.play();
        if (result && typeof result.catch === 'function') {
          result.catch(function () {});
        }
      }
    });

    syncButtons(control, shouldPause);
  });

  // Keep the button state in sync when the theme itself plays or pauses a video
  // (e.g. slideshow slide change, or scrolled out of view).
  function wirePlaybackSync(control) {
    getVideos(control).forEach(function (video) {
      video.addEventListener('play', function () {
        syncButtons(control, false);
      });
      video.addEventListener('pause', function () {
        if (!video.seeking) syncButtons(control, true);
      });
    });
  }

  function init() {
    document.querySelectorAll(CONTROL_SELECTOR).forEach(wirePlaybackSync);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
